/* global SideDrawer */

if (!customElements.get('quick-add-drawer')) {
  class QuickAddDrawer extends SideDrawer {
    constructor() {
      super();
      this.content = this.querySelector('.js-product-details');
      this.footer = this.querySelector('.drawer__footer');
      this.form = this.querySelector('product-form');
      this.notification = this.querySelector('.js-added-to-cart');
      this.backBtn = this.querySelector('.drawer__back-btn');
      this.openCartDrawerLinks = this.querySelectorAll('.js-open-cart-drawer');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.fetch = null;
      this.fetchedUrls = [];
      this.quickAddButtonMouseEnterHandler = this.handleQuickAddButtonMouseEnter.bind(this);
      this.documentClickHandler = this.handleDocumentClick.bind(this);

      document.addEventListener('click', this.documentClickHandler);
      this.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      // T1: the panel's trust items open the page's own info sheets (so-pdp-info cards), when the page has them.
      this.addEventListener('click', (evt) => {
        const item = evt.target.closest && evt.target.closest('[data-so-qa-info]');
        if (!item) return;
        const card = Array.from(document.querySelectorAll(`so-pdp-info [data-so-open="${item.dataset.soQaInfo}"]`)).find((c) => !this.contains(c));
        if (card) { evt.preventDefault(); card.click(); }
      });

      this.openCartDrawerLinks.forEach((link) => {
        link.addEventListener('click', this.handleOpenCartClick.bind(this));
      });

      if (theme.device.hasHover && theme.mediaMatches.md) {
        document.querySelectorAll('.js-quick-add').forEach((button) => {
          this.bindQuickAddButtonMouseEnter(button);
        });

        if ('MutationObserver' in window) {
          this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              // Add event listener to new buttons
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    this.bindQuickAddButtonMouseEnter(button);
                  });
                }
              });

              // Remove event listener from removed buttons
              mutation.removedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
                  });
                }
              });
            });
          });

          // Start observing changes to the DOM
          this.observer.observe(document.body, { childList: true, subtree: true });
        }
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.documentClickHandler);
      document.querySelectorAll('.js-quick-add').forEach((button) => {
        button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      });
      if (this.observer) this.observer.disconnect();
    }

    /**
     * Bind a mouseenter event to a given quick add button if it's not already bound
     * @param {Element} button - The button element to bind the mouseenter event to.
     */
    bindQuickAddButtonMouseEnter(button) {
      if (!button.dataset.quickAddListenerAdded) {
        button.dataset.quickAddListenerAdded = 'true';
        button.addEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      }
    }

    /**
     * Start the fetch on hover of a quick add button for perceived performance gains
     * @param {object} evt - Event object.
     */
    handleQuickAddButtonMouseEnter(evt) {
      if (!this.fetchedUrls.includes(evt.target.dataset.productUrl)) {
        this.fetch = {
          url: evt.target.dataset.productUrl,
          promise: fetch(evt.target.dataset.productUrl)
        };
        this.fetchedUrls.push(evt.target.dataset.productUrl);
      }
    }

    /**
     * Handles 'click' events on success notification's 'View cart' link
     * @param {object} evt - Event object.
     */
    handleOpenCartClick(evt) {
      // Open the cart drawer if available on the page
      if (this.cartDrawer) {
        evt.preventDefault();
        this.cartDrawer.open();
      } else if (window.location.pathname === theme.routes.cart) {
        evt.preventDefault();
        this.close();
      }
    }

    /**
     * Handles 'click' events on the document.
     * @param {object} evt - Event object.
     */
    handleDocumentClick(evt) {
      if (!evt.target.matches('.js-quick-add')) return;

      // Close the cart drawer if it's open
      if (this.cartDrawer && this.cartDrawer.ariaHidden === 'false') {
        const overlay = document.querySelector('.js-overlay.is-visible');
        if (overlay) overlay.style.transitionDelay = '200ms';

        this.cartDrawer.close();

        // Wait a few ms for a more optimal ux/animation
        setTimeout(() => {
          this.backBtn.hidden = false;
          this.open(evt.target);
          if (overlay) overlay.style.transitionDelay = '';
        }, 200);
      } else {
        this.open(evt.target);
      }
    }

    /**
     * Handles 'on:variant:change' events on the Quick Add drawer.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      let url = this.productUrl;

      if (evt.detail.variant) {
        const separator = this.productUrl.split('?').length > 1 ? '&' : '?';
        url += `${separator}variant=${evt.detail.variant.id}`;
      }

      this.querySelectorAll('.js-prod-link').forEach((link) => {
        link.href = url;
      });
    }

    /**
     * Opens the drawer and fetches the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    async open(opener) {
      opener.setAttribute('aria-disabled', 'true');

      if (this.notification) this.notification.hidden = true;

      // If it's the same product as previously shown, there's no need to re-fetch the details.
      if (this.productUrl && this.productUrl === opener.dataset.productUrl) {
        super.open(opener);
        opener.removeAttribute('aria-disabled');
        return;
      }

      this.productUrl = opener.dataset.productUrl;
      this.content.innerHTML = '';
      this.classList.add('is-loading');
      this.content.classList.add('drawer__content--out');
      this.footer.classList.add('drawer__footer--out');

      super.open(opener);

      if (!this.fetch || this.fetch.url !== opener.dataset.productUrl) {
        this.fetch = {
          url: opener.dataset.productUrl,
          promise: fetch(opener.dataset.productUrl)
        };
      }

      const response = await this.fetch.promise;
      if (response.ok) {
        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();
        this.productEl = tmpl.content.querySelector('.cc-main-product .js-product');
        this.pageEl = tmpl.content;   // T1: the mini PDP reads the spec tiles and the review highlight from the same fetch
        this.renderProduct(opener);
      }

      this.fetch = null;

      opener.removeAttribute('aria-disabled');
    }

    /**
     * Closes the cart drawer.
     */
    close() {
      super.close(() => {
        this.backBtn.hidden = true;
      });
    }

    /**
     * Renders the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    renderProduct(opener) {
      // Replace instances of section id to prevent duplicates on the product page.
      const sectionId = this.productEl.dataset.section;
      this.productEl.innerHTML = this.productEl.innerHTML.replaceAll(sectionId, 'quickadd');

      // Prevent variant picker from updating the URL on change.
      const variantPicker = this.productEl.querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.dataset.updateUrl = 'false';
        this.selectFirstVariant = variantPicker.dataset.selectFirstVariant === 'true';
      }

      // Remove size chart modal and link (if they exist).
      const sizeChartModal = this.productEl.querySelector('[data-modal="size-chart"]');
      if (sizeChartModal) {
        sizeChartModal.remove();
      }

      this.updateContent();
      this.updateForm();

      // Update the product media.
      const activeMedia = this.productEl.querySelector('.media-viewer__item.is-current-variant');
      if (activeMedia) this.updateMedia(activeMedia.dataset.mediaId);

      if (opener.dataset.selectedColor && this.selectFirstVariant) {
        // Timeout to allow the VariantPicker to initialize
        setTimeout(this.setActiveVariant.bind(this, opener), 10);
      }
    }

    /**
     * Set color variant to match the one selected in the card.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    setActiveVariant(opener) {
      const colorOptionBox = this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`);
      if (colorOptionBox) {
        this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`)
          .click();
      } else {
        const colorOptionDropdown = this.querySelector(
          `.custom-select__option[data-value="${opener.dataset.selectedColor}"]`
        );
        if (colorOptionDropdown) {
          const customSelect = colorOptionDropdown.closest('custom-select');
          customSelect.selectOption(colorOptionDropdown);
        }
      }
    }

    /**
     * Updates the product media.
     * @param {string} mediaId - Id of the media item to show.
     */
    updateMedia(mediaId) {
      const img = this.productEl.querySelector(`[data-media-id="${mediaId}"] img`);
      if (!img) return;

      const src = img.src ? img.src.split('&width=')[0] : img.dataset.src.split('&width=')[0];
      const container = this.querySelector('.quick-add-info__media');
      const width = container.offsetWidth;
      const aspectRatio = img.width / img.height;

      container.innerHTML = `
        <img src="${src}&width=${width}" srcset="${src}&width=${width}, ${src}&width=${width * 2} 2x" width="${width * 2}" height="${(width * 2) / aspectRatio}" alt="${img.alt}">
      `;
    }

    /**
     * Builds the markup for the drawer content element.
     */
    updateContent() {
      let weightElem = this.getElementHtml('.product-info__weight');
      if (weightElem && weightElem.length > 0) {
        weightElem = `<div class="product-info__weight text-sm mt-2">${weightElem}</div>`;
      }

      // T1 (2026-10-01): the panel speaks the PDP's language. Header = the product's short title; top block = image
      // (96px, the current variant) + the card spec line + the PDP's own price row (price, struck price, "· unit",
      // rating); pickers in a .so-buy-module wrapper so they look exactly like the PDP's; then stock, the flavour blurb
      // and a quiet link to the full page. Falls back to the old title / price elements when the fetched product page
      // has no so-buybox-header pieces.
      const h1 = this.productEl.querySelector('.so-bbh__title, .product-title');
      const heading = this.querySelector('[data-so-qa-title]');
      if (heading) heading.textContent = (h1 && h1.textContent.replace(/\s+/g, ' ').trim()) || heading.dataset.default || '';
      const spec = this.productEl.querySelector('.so-bbh__spec');
      const priceRow = this.productEl.querySelector('.so-bbh__row');
      // Mini PDP (2026-10-01): parts of the product page that came with this same fetch — nothing extra is requested and
      // no logic is copied: the page's own spec tiles, its first review card (the PDP's pick, current flavour first) and
      // the labels/icons of its trust cards. Text goes through esc(); markup is the page's own server-rendered HTML.
      const esc = (t) => { const d = document.createElement('div'); d.textContent = t == null ? '' : String(t); return d.innerHTML; };
      const page = this.pageEl;
      const tiles = page && page.querySelector('.so-specs__dl--tiles');
      const rv = page && page.querySelector('#so-reviews.so-rvp');
      let review = '';
      if (rv && Number(rv.dataset.total) > 0) {
        const q = rv.querySelector('.so-rvp__pick .sov__q');
        const by = rv.querySelector('.so-rvp__pick .sov__by b');
        review = `<div class="so-qa__review"><p class="so-qa__review-h"><span class="so-qa__star" aria-hidden="true">★</span> <b>${esc(rv.dataset.avg)}</b> <span>from ${esc(rv.dataset.total)} review${Number(rv.dataset.total) === 1 ? '' : 's'}</span></p>${q ? `<blockquote>${esc(q.textContent.trim())}</blockquote>` : ''}${q && by ? `<p class="so-qa__review-by">${esc(by.textContent.trim())}</p>` : ''}</div>`;
      }
      const uspIcons = Array.from(this.productEl.querySelectorAll('so-pdp-info .usp-card .usp-card__icon')).slice(0, 3).map((i) => i.outerHTML);
      const uspLabels = ['Delivery & collection', 'Secure checkout', 'Moneyback guarantee'];
      // each item opens the SAME info sheet as the trust cards (the page's own so-pdp-info — the PDP's, or the site-wide
      // footer strip's); on a page without one it is a link to the product page.
      const uspKeys = ['delivery', 'payments', 'guarantee'];
      const trust = uspIcons.length === 3 ? `<ul class="so-qa__trust">${uspLabels.map((l, i) => `<li><a class="so-qa__trust-b js-prod-link" href="${this.productUrl}" data-so-qa-info="${uspKeys[i]}">${uspIcons[i]}<span>${l}</span></a></li>`).join('')}</ul>` : '';
      this.content.innerHTML = `
        <div class="so-qa__top">
          <a class="quick-add-info__media so-qa__img js-prod-link${theme.settings.blendProductImages ? ' image-blend' : ''}" href="${this.productUrl}" tabindex="-1" aria-hidden="true"></a>
          <div class="so-qa__meta so-qa__meta--spec">
            ${spec ? spec.outerHTML : ''}
            ${weightElem || ''}
          </div>
          <div class="so-qa__meta so-qa__meta--price">
            ${priceRow ? priceRow.outerHTML : `<div class="product-price">${this.getElementHtml('.product-price')}</div>`}
          </div>
        </div>
        <div class="so-buy-module so-qa__picker">
          <div class="product-options">
            ${this.getElementHtml('.product-options', '.custom-option:not(.quick-add-hidden)')}
            ${this.getOuterHtml('so-variant-drawer')}
            ${this.getOuterHtml('#so-colour-map')}
          </div>
        </div>
        <div class="quick-add__inventory">
          ${this.getOuterHtml('product-inventory')}
        </div>
        <div class="quick-add__flavour-description">
          ${this.getOuterHtml('.flavour-description')}
        </div>
        ${tiles ? `<div class="so-qa__specs">${tiles.outerHTML}</div>` : ''}
        ${review}
        ${trust}
        <p class="so-qa__more"><a href="${this.productUrl}" class="so-qa__details js-prod-link">View full details ›</a></p>
        <div class="product-backorder">
          ${this.getElementHtml('.product-backorder')}
        </div>
      `;
      // the rating chip points at the product page's reviews, not at an anchor on this page
      this.content.querySelectorAll('.so-bbh__chip').forEach((c) => { c.setAttribute('href', `${this.productUrl.split('#')[0]}#judgeme_product_reviews`); });

      this.classList.remove('is-loading');
      this.content.classList.remove('drawer__content--out');

      // Sweep any variant-drawer overlays portaled to <body> by a previous quick-add open, then
      // upgrade the freshly-injected drawer(s) (they miss the DOMContentLoaded scan). Text options
      // route to the searchable drawer; colour/swatch options render inline and inject nothing.
      document.querySelectorAll('body > [data-so-vd-qa]').forEach((el) => el.remove());
      this.querySelectorAll(':scope > [data-so-vd-qa]').forEach((el) => el.remove());
      if (window.SoVariantDrawer) window.SoVariantDrawer.initRoot(this.content);
      // T1: the picker list opens INSIDE this panel, not as a second overlay — the variant drawer portals its list and
      // backdrop to <body>; bring both back in here (so-quick-add.css positions them). 2026-10-13 (Charl: "all variant
      // pickers to match the old style"): every option is live's closed row; its card list opens in the panel.
      document.querySelectorAll('body > .so-vd__drawer[data-so-vd-qa], body > .so-vd__backdrop[data-so-vd-qa]').forEach((el) => this.appendChild(el));
      if (window.soPdp) { try { window.soPdp.chips(this.content); } catch (e) { /* the plain pickers still work */ } }
      // Wire the copied flavour description to this slide-up's picker. (product-inventory is a
      // custom element and upgrades itself on injection.)
      if (window.FlavourDescription) window.FlavourDescription.initRoot(this.content);
    }

    /**
     * Updates the Quick Add drawer form (buy buttons).
     */
    updateForm() {
      const productForm = this.productEl.querySelector('product-form');
      this.footer.classList.remove('quick-add__footer-message');

      if (productForm) {
        this.form.innerHTML = productForm.innerHTML;
        this.form.init();

        if (Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
      } else {
        const signUpForm = this.productEl.querySelector('.product-signup');
        if (signUpForm) {
          this.form.innerHTML = signUpForm.innerHTML;
        } else {
          this.footer.classList.add('quick-add__footer-message');
          this.form.innerHTML = `
            <div class="alert quick-add__alert bg-info-bg text-info-text">
              <div class="flex">
                <div>
                  <svg class="icon icon--price_tag" width="32" height="32" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                    <path fill="currentColor" d="M7.59 1.34a1 1 0 01.7-.29h5.66a1 1 0 011 1v5.66a1 1 0 01-.3.7L7.6 15.5a1 1 0 01-1.42 0L.52 9.83a1 1 0 010-1.42l7.07-7.07zm6.36 6.37l-7.07 7.07-5.66-5.66L8.3 2.05h5.66v5.66z" fill-rule="evenodd"/>
                    <path fill="currentColor" d="M9.7 6.3a1 1 0 101.42-1.42 1 1 0 00-1.41 1.41zM9 7a2 2 0 102.83-2.83A2 2 0 009 7z" fill-rule="evenodd"/>
                  </svg>
                </div>
                <div>
                  <p class="text-h6 font-bold mb-2">${theme.strings.awaitingSale}</p>
                  <a class="link js-prod-link" href="${this.productUrl}">${theme.strings.viewDetails}</a>
                </div>
              </div>
            </div>`;
        }
      }

      this.footer.classList.remove('drawer__footer--out');
    }

    /**
     * Gets the innerHTML of elements within the product element.
     * @param {string} selector - CSS selector for the element.
     * @param {...*} additionalSelectors - Additional CSS selectors for elements.
     * @returns {?string}
     */
    getElementHtml(selector, ...additionalSelectors) {
      const selectors = [selector].concat(additionalSelectors);
      let html = '';
      selectors.forEach((sel) => {
        const els = this.productEl.querySelectorAll(sel);
        Array.from(els).forEach((el) => {
          html += el.innerHTML;
        });
      });
      return html;
    }

    /**
     * Gets the OUTER html of elements within the product element (keeps the wrapper tag — needed
     * for custom elements like <so-variant-drawer> whose tag/attributes carry the behaviour).
     * @param {string} selector - CSS selector for the element.
     * @returns {string}
     */
    getOuterHtml(selector) {
      let html = '';
      Array.from(this.productEl.querySelectorAll(selector)).forEach((el) => {
        html += el.outerHTML;
      });
      return html;
    }

    /**
     * Shows an "Added to cart" message in the drawer.
     */
    addedToCart() {
      if (this.notification) {
        setTimeout(() => {
          this.notification.hidden = false;
        }, 300);

        setTimeout(() => {
          this.notification.hidden = true;
        }, this.notification.dataset.visibleFor);
      }
    }
  }

  customElements.define('quick-add-drawer', QuickAddDrawer);
}
